import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Check, Eye, EyeOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import {
  getAllTestimonials,
  createTestimonial,
  updateTestimonial,
  deactivateTestimonial,
  deleteTestimonial
} from '../../service/testimonial.service';

import SecondaryFooter from '../Common/SecondaryFooter';
import AdminLayout from '../Common/AdminLayout';
import './TestimonialManagement.css';

const TestimonialManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // ✅ NEW: Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: '' // 'success', 'error', 'info', 'warning'
  });

  const [formData, setFormData] = useState({
    comment: '',
    name: '',
    org: '',
    designation: '',
    product: '',
    modifiedName: '',
    isActive: true
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // ✅ NEW: Show toast notification function
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 5000);
  };

  const fetchTestimonials = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Failed to load testimonials');
      showToast('Failed to load testimonials', 'error'); // ✅ CHANGED: alert → toast
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.comment.trim()) {
      errors.comment = 'Comment is required';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleOpenModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData({
        comment: testimonial.comment || '',
        name: testimonial.name || '',
        org: testimonial.org || '',
        designation: testimonial.designation || '',
        product: testimonial.product || '',
        modifiedName: testimonial.modifiedName || '',
        isActive: testimonial.isActive !== undefined ? testimonial.isActive : true
      });
    } else {
      setEditingTestimonial(null);
      setFormData({
        comment: '',
        name: '',
        org: '',
        designation: '',
        product: '',
        modifiedName: '',
        isActive: true
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
    setFormData({
      comment: '',
      name: '',
      org: '',
      designation: '',
      product: '',
      modifiedName: '',
      isActive: true
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast('Please fill in all required fields', 'error'); // ✅ NEW: Validation toast
      return;
    }

    try {
      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial.id, formData);
        showToast('Testimonial updated successfully!', 'success'); // ✅ CHANGED: alert → toast
      } else {
        await createTestimonial(formData);
        showToast('Testimonial created successfully!', 'success'); // ✅ CHANGED: alert → toast
      }
      
      handleCloseModal();
      fetchTestimonials();
    } catch (err) {
      console.error('Error saving testimonial:', err);
      
      // ✅ CHANGED: Better error handling
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.detail || 
                          'Failed to save testimonial. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const testimonial = testimonials.find(t => t.id === id);
      if (testimonial) {
        await updateTestimonial(id, { ...testimonial, isActive: !currentStatus });
        showToast(
          `Testimonial ${!currentStatus ? 'activated' : 'deactivated'} successfully!`, 
          'success'
        ); // ✅ CHANGED: alert → toast
        fetchTestimonials();
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      showToast('Failed to update status', 'error'); // ✅ CHANGED: alert → toast
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTestimonial(id);
      showToast('Testimonial deleted successfully!', 'success'); // ✅ CHANGED: alert → toast
      setDeleteConfirmId(null);
      fetchTestimonials();
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      showToast('Failed to delete testimonial', 'error'); // ✅ CHANGED: alert → toast
    }
  };

  return (
    <AdminLayout>
      <div className="testimonial-management">
        
        {/* ✅ NEW: Toast Notification Component */}
        {toast.show && (
          <div className={`toast-notification toast-${toast.type}`}>
            <div className="toast-icon">
              {toast.type === 'success' && <CheckCircle size={20} />}
              {toast.type === 'error' && <XCircle size={20} />}
              {toast.type === 'warning' && <AlertCircle size={20} />}
              {toast.type === 'info' && <AlertCircle size={20} />}
            </div>
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close"
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        <div className="admin-container">
          <div className="admin-header">
            <h1>Testimonial Management</h1>
            <button className="btn-create" onClick={() => handleOpenModal()}>
              <Plus size={20} />
              Add New Testimonial
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button className="btn-retry" onClick={fetchTestimonials}>
                Retry
              </button>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="empty-state">
              <p>No testimonials found. Create your first one!</p>
            </div>
          ) : (
            <div className="testimonials-grid">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className={`testimonial-card-admin ${!testimonial.isActive ? 'inactive' : ''}`}
                >
                  <div className="card-header-admin">
                    <div className="status-badge">
                      {testimonial.isActive ? (
                        <span className="badge-active">Active</span>
                      ) : (
                        <span className="badge-inactive">Inactive</span>
                      )}
                    </div>
                    <div className="card-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleActive(testimonial.id, testimonial.isActive)}
                        title={testimonial.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {testimonial.isActive ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleOpenModal(testimonial)}
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => setDeleteConfirmId(testimonial.id)}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="card-body-admin">
                    <p className="testimonial-comment">"{testimonial.comment}"</p>
                    
                    <div className="testimonial-author">
                      <strong>{testimonial.name}</strong>
                      {testimonial.designation && (
                        <span className="author-designation">{testimonial.designation}</span>
                      )}
                      {testimonial.org && (
                        <span className="author-org">{testimonial.org}</span>
                      )}
                    </div>

                    {testimonial.product && (
                      <div className="testimonial-product">
                        <span className="product-label">Product:</span>
                        <span className="product-name">{testimonial.product}</span>
                      </div>
                    )}

                    <div className="testimonial-meta">
                      <small>
                        Created: {new Date(testimonial.createdDate).toLocaleDateString()}
                      </small>
                    </div>
                  </div>

                  {deleteConfirmId === testimonial.id && (
                    <div className="delete-confirm">
                      <p>Are you sure you want to delete this testimonial?</p>
                      <div className="confirm-actions">
                        <button
                          className="btn-confirm-delete"
                          onClick={() => handleDelete(testimonial.id)}
                        >
                          Yes, Delete
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={() => setDeleteConfirmId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal for Create/Edit */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingTestimonial ? 'Edit Testimonial' : 'Create New Testimonial'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  <X size={24} />
                </button>
              </div>

              <form className="modal-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="comment">
                    Comment <span className="required">*</span>
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    className={formErrors.comment ? 'error' : ''}
                    placeholder="Enter testimonial comment"
                    rows="4"
                    required
                  />
                  {formErrors.comment && (
                    <span className="error-text">{formErrors.comment}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={formErrors.name ? 'error' : ''}
                      placeholder="Enter name"
                      required
                    />
                    {formErrors.name && (
                      <span className="error-text">{formErrors.name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="org">Organization</label>
                    <input
                      type="text"
                      id="org"
                      name="org"
                      value={formData.org}
                      onChange={handleInputChange}
                      placeholder="Enter organization"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="designation">Designation</label>
                    <input
                      type="text"
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="Enter designation"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="product">Product</label>
                    <input
                      type="text"
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleInputChange}
                      placeholder="Enter product name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="modifiedName">Modified Name (Optional)</label>
                  <input
                    type="text"
                    id="modifiedName"
                    name="modifiedName"
                    value={formData.modifiedName}
                    onChange={handleInputChange}
                    placeholder="e.g., John D."
                  />
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="isActive">Active (visible on homepage)</label>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    {editingTestimonial ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <SecondaryFooter />
        
      </div>
    </AdminLayout>
  );
};

export default TestimonialManagement;